import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { useForm, useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { useCategoriesData } from '../../../hooks/useCategoriesData';
import {
  setAdriverSelectedCity,
  setCreationResetButtonIsDisabled,
  setCurrentCity,
  toggleAuthWindow
} from '../../../actions';
import { Photos } from './blocks/Photos';
import {
  BUILDING_TYPE_ID,
  Cities,
  FORM_VALUES,
  INITIAL_FORM_VALUES
} from '../../../utils/helpers/constants';
import { CompanyTargetTypeEnum } from '../../../graphql/types';
import { getCategoryValues } from '../../../utils/helpers/common';
import CONFIG from '../../../config';
import { sendAccessToken } from '../../../api/mutations';
import { Button } from '../../ui/Button';
import { ItemCreationLayout } from '../ItemCreationLayout';
import { AddNewItemFormProps } from './types';
import { ButtonType } from '../../../styled/theme/types';
import {
  selectAddNewItemModalIsOpenAuthWindow,
  selectAddNewItemModalResetButtonIsDisabled
} from '../../../store/selectors';
import {
  AuthIframe,
  AuthWrapper,
  ModalContentWrapper,
  resetButtonStyles
} from './styled';
import { TermsAndCondition } from './components/TermsAndCondition';
import useScreenSize from '../../../hooks/useScreenSize';
import { MainFormBlock } from './blocks/MainFormBlock';
import { AboutEstate } from './blocks/AboutEstate';
import { Description } from './blocks/Description';
import { scrollToErrorField } from './scrollToErrorField';
import { Pricing } from './blocks/Pricing';
import { Contacts } from './blocks/Contacts';
import { ErrorPopup } from '../../ErrorPopup';
import { CreatePageStepsEnum } from '../types';

const AddNewItemFormComponent: React.FC<AddNewItemFormProps> = ({
  profile,
  errorText,
  categories,
  setErrorText,
  createNewItem,
  failedModalIsOpen,
  toggleFailedModal,
  returnToURL,
  loading,
  setLoading,
  disableSubmitButton,
  setPageStep
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const form = useForm();
  const { reset, submit, change } = form;
  const { input: phoneField } = useField(FORM_VALUES.phone, {
    subscription: { value: true }
  });
  const { input: categoryIdField } = useField(FORM_VALUES.categoryId, {
    subscription: { value: true }
  });
  const { input: typeEstateField } = useField(FORM_VALUES.typeEstate, {
    subscription: { value: true }
  });
  const { isSmall } = useScreenSize();

  const [photoLoading, setPhotoLoading] = useState(false);
  const [citiesLoading, setCitiesIsLoading] = useState(false);

  const [mutateToken] = useMutation(sendAccessToken);

  useCategoriesData({
    categoryId: categoryIdField.value,
    typeEstate: typeEstateField.value,
    change
  });

  const creationResetButtonIsDisabled = useSelector(
    selectAddNewItemModalResetButtonIsDisabled
  );
  const isOpenAuthWindow = useSelector(selectAddNewItemModalIsOpenAuthWindow);

  const closeWindow = () => {
    dispatch(toggleAuthWindow(false));
  };

  const validateAndSubmit = useCallback(async () => {
    await submit();
    const { errors } = form.getState();
    if (errors && Object.keys(errors).length > 0) {
      scrollToErrorField();
      setLoading(false);
    }
  }, [submit, form, setLoading]);

  const handleTokenSuccess = (res, token) => {
    if (!res.data?.signIn?.errors?.length) {
      createNewItem({
        accessToken: token,
        values: form.getState().values
      });
    } else {
      dispatch(setCreationResetButtonIsDisabled(false));
      setLoading(false);
      toggleFailedModal();
    }
  };

  const handleTokenError = reason => {
    dispatch(setCreationResetButtonIsDisabled(false));
    toggleFailedModal();
    setLoading(false);
    console.error('error in validate auth token', reason);
  };

  const handleToken = token => {
    if (!token) return;
    closeWindow();
    mutateToken({
      variables: { accessToken: token }
    }).then(res => handleTokenSuccess(res, token), handleTokenError);
  };

  const handleAuth = useCallback(
    (event: MessageEvent) => {
      if (event.data.name === 'authenticationFinished') {
        setLoading(true);
        dispatch(setCreationResetButtonIsDisabled(true));
        handleToken(event.data.access_token);
      } else if (event.data === 'authenticationModalClose') {
        closeWindow();
        setLoading(false);
        dispatch(setCreationResetButtonIsDisabled(false));
      }
    },
    [dispatch, setLoading]
  );

  useEffect(() => {
    window.addEventListener('message', handleAuth);

    return () => window.removeEventListener('message', handleAuth);
  }, [handleAuth]);

  const formResetHandler = useCallback(() => {
    const defaultCategoryId = BUILDING_TYPE_ID.FLAT_ALL;

    const categoryValues = getCategoryValues({
      id: defaultCategoryId,
      categories
    });
    const selectedCategory = categories.find(
      category => category.id === defaultCategoryId
    );

    let resetData = {
      ...INITIAL_FORM_VALUES,
      [FORM_VALUES.categoryId]: BUILDING_TYPE_ID.FLAT_NEW,
      [FORM_VALUES.categoryValues]: categoryValues,
      [FORM_VALUES.typeEstate]: selectedCategory?.name
    };

    const currentValues = form.getState().values;

    if (profile) {
      const { name, phone, email, owner } = currentValues || {};

      resetData = {
        ...resetData,
        [FORM_VALUES.name]: name,
        [FORM_VALUES.phone]: phone,
        [FORM_VALUES.email]: email,
        ...(profile.targetType === 'AGENCY' && { [FORM_VALUES.owner]: owner })
      };
    }

    dispatch(setCurrentCity(Cities.Baku.id));
    dispatch(setAdriverSelectedCity(Cities.Baku.az));
    reset(resetData);
    setPageStep(CreatePageStepsEnum.DealType);
  }, [categories, profile, dispatch, reset, setPageStep, form]);

  const onHideHandleClick = useCallback(() => {
    if (!loading) {
      try {
        if (returnToURL) {
          const returnUrl = new URL(returnToURL);
          if (
            returnUrl.origin === window.location.origin &&
            returnUrl.pathname !== router.pathname
          ) {
            router.replace(returnUrl.pathname);
            return;
          }
        }
      } catch {
        router.back();
        return;
      }
      router.back();
    }
  }, [loading, returnToURL, router]);

  const clearErrorAndClose = () => {
    setErrorText('');
    toggleFailedModal();
  };

  const returnToUrl =
    typeof window !== 'undefined' ? btoa(window.location.href) : '';
  const authUrl = `${CONFIG.authAppUrl}/phone-confirmation?phone=${
    phoneField.value
  }&return_to=${returnToUrl}`;

  const resetButton = (modalButton = false) => (
    <Button
      text={isSmall ? t('common.reset') : t('new_ad.reset')}
      onClick={formResetHandler}
      state={ButtonType.link}
      disabled={creationResetButtonIsDisabled}
      cypress="item-creation-form-reset-btn"
      stat="item-creation-form-reset-btn"
      styles={resetButtonStyles(modalButton)}
    />
  );

  const leftModalBlock = useMemo(
    () => resetButton(),
    [isSmall, t, creationResetButtonIsDisabled, formResetHandler]
  );
  const modalHeaderRightBlock = useMemo(
    () => resetButton(true),
    [isSmall, t, creationResetButtonIsDisabled, formResetHandler]
  );

  const termsAndCondition = useMemo(
    () => (
      <TermsAndCondition
        onSubmit={validateAndSubmit}
        isLoading={loading}
        isDisabled={citiesLoading || photoLoading || disableSubmitButton}
        buttonCypress="new-ad-form-submit-button"
      />
    ),
    [
      validateAndSubmit,
      loading,
      citiesLoading,
      photoLoading,
      disableSubmitButton
    ]
  );

  return (
    <ItemCreationLayout
      cypress="item-creation-form"
      onClose={onHideHandleClick}
      contentCypress="item-modal__content"
      isFormStep
      modalHeaderRightBlock={modalHeaderRightBlock}
      leftModalBlock={leftModalBlock}
      isModalHeaderHidden={isOpenAuthWindow}
    >
      <ModalContentWrapper>
        <MainFormBlock
          citiesLoading={citiesLoading}
          setCitiesIsLoading={setCitiesIsLoading}
          categories={categories}
        />
        <AboutEstate />
        <Photos loading={photoLoading} setLoading={setPhotoLoading} />
        <Pricing
          isResidence={profile?.targetType === CompanyTargetTypeEnum.Residence}
        />
        <Description />
        <Contacts termsBlock={isSmall && termsAndCondition} />
        <ErrorPopup
          isOpen={failedModalIsOpen}
          onClose={clearErrorAndClose}
          title={t('payments.errors.title')}
          message={errorText || t('common.something_went_wrong')}
        />
        {!isSmall && termsAndCondition}
        {isOpenAuthWindow && (
          <AuthWrapper onClick={closeWindow}>
            <AuthIframe
              title="auth"
              src={authUrl}
              onClick={e => e.stopPropagation()}
            />
          </AuthWrapper>
        )}
      </ModalContentWrapper>
    </ItemCreationLayout>
  );
};

export const AddNewItemForm = memo(AddNewItemFormComponent);
